import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { BookOpen, Users, Compass, Clock } from 'lucide-react';

const LearningModel = () => {
 const modelFeatures = [
 {
 icon: <BookOpen size={28} />,
 title: 'Mastery-Based Academics',
 description: 'Children progress through foundational literacy and numeracy at their own pace. No moving forward until concepts are truly understood. No being held back when ready to advance.'
 },
 {
 icon: <Compass size={28} />,
 title: 'Personalised Technology',
 description: 'Learning platforms track progress, identify gaps, and suggest pathways—but Walezi make all final decisions. Technology enhances insight; humans provide wisdom.'
 },
 {
 icon: <Users size={28} />,
 title: 'Mixed-Age Learning Pods',
 description: 'Children learn in multi-age communities where older learners mentor younger ones, and everyone progresses based on readiness, not birthdate.'
 },
 {
 icon: <Clock size={28} />,
 title: 'Inquiry-Led Exploration',
 description: 'Learning follows curiosity. Questions drive investigation. Children develop the confidence to explore, test ideas, and learn from mistakes in a supportive environment.'
 }
 ];

 return (
 <div className="learning-model-page">
 <section className="page-hero">
 <div className="section-container">
 <h1 className="page-title">Learning Model</h1>
 <p className="page-subtitle">
 A coherent system where technology, pedagogy and real-world learning work together.
 </p>
 </div>
 </section>

 <section className="model-features-section">
 <div className="section-container">
 <div className="features-grid">
 {modelFeatures.map((feature, index) => (
 <Card key={index} className="feature-card">
 <CardContent className="feature-content">
 <div className="feature-icon">
 {feature.icon}
 </div>
 <h3>{feature.title}</h3>
 <p>{feature.description}</p>
 </CardContent>
 </Card>
 ))}
 </div>
 <div className="learning-materials-showcase">
 <div className="materials-grid">
 <div className="material-image">
 <img src="/montessori-materials.jpg" alt="Natural Montessori learning materials" />
 <p className="image-caption">Natural sensory materials for hands-on learning</p>
 </div>
 <div className="material-image">
 <img src="/wooden-blocks.jpg" alt="Wooden educational blocks" />
 <p className="image-caption">Wooden blocks for literacy and numeracy development</p>
 </div>
 <div className="material-image">
 <img src="/tech-learning.jpg" alt="Technology-supported learning" />
 <p className="image-caption">Personalised technology enhances insight and tracking</p>
 </div>
 </div>
 </div>
 </div>
 </section>

 <section className="daily-structure-section">
 <div className="section-container">
 <h2 className="section-title">A Clear Day-to-Day Structure</h2>
 <div className="daily-structure-content">
 <Card className="structure-card morning">
 <CardContent className="structure-content">
 <h3>Morning: Core Learning Block</h3>
 <p>
 A protected two-hour session focused on foundational academics—literacy, numeracy, 
 and essential knowledge. Personalised progression. Deep focus. Mastery before moving forward.
 </p>
 </CardContent>
 </Card>

 <Card className="structure-card afternoon">
 <CardContent className="structure-content">
 <h3>Afternoon: Real-World Application</h3>
 <p>
 The remainder of the day is dedicated to applying knowledge in real contexts:
 </p>
 <ul className="application-list">
 <li>Life skills and independence</li>
 <li>Financial literacy and enterprise</li>
 <li>Agriculture and sustainability</li>
 <li>Trade, craft and problem-solving</li>
 <li>Leadership, collaboration and reflection</li>
 </ul>
 </CardContent>
 </Card>
 </div>
 </div>
 </section>

 <section className="learning-environment-section">
 <div className="section-container">
 <h2 className="section-title">Comfortable & Flexible Learning Spaces</h2>
 <div className="learning-env-content">
 <p className="env-intro">
 Nova Roots provides varied, comfortable seating options—floor cushions, bean bags, different height tables, and cosy reading nooks. Children choose where and how they learn best, supporting focus, comfort, and individual learning styles.
 </p>
 <div className="env-images-grid">
 <div className="env-image">
 <img src="/flexible-seating.jpg" alt="Flexible seating with bean bags" />
 </div>
 <div className="env-image">
 <img src="/learning-space.jpg" alt="Comfortable learning space with varied furniture" />
 </div>
 </div>
 </div>
 </div>
 </section>

 <section className="approach-section">
 <div className="section-container">
 <div className="approach-box">
 <h2>Rigour Without Burnout. Relevance Without Chaos.</h2>
 <p>
 This structure ensures that children build strong academic foundations whilst staying 
 connected to the real world. Learning is purposeful, not performative.
 </p>
 </div>
 </div>
 </section>

 <section className="faith-section">
 <div className="section-container">
 <h2 className="section-title">Optional Personal Faith & Worldview Learning</h2>
 <div className="faith-content">
 <p>
 Nova Roots welcomes children from all backgrounds and recognises that faith and belief 
 are deeply personal journeys. The school does not promote or teach religion.
 </p>
 <p>
 Families may choose optional, faith-aligned study for their child, supported through 
 personalised, AI-assisted learning pathways. This may include Christian Bible study, 
 Islamic studies, or other faith or worldview-based learning.
 </p>
 <div className="faith-principles">
 <p><strong>This approach ensures:</strong></p>
 <ul>
 <li>Respect for individual belief systems</li>
 <li>No hierarchy of religions</li>
 <li>No classroom-based proselytising</li>
 <li>Inclusive shared learning spaces</li>
 </ul>
 </div>
 <p className="faith-note">
 Ethics and character are grounded in shared human values: dignity, responsibility, 
 honesty and care.
 </p>
 </div>
 </div>
 </section>
 </div>
 );
};

export default LearningModel;